import { ChipToggleGroup, ChipToggleOption } from '@app/components/chip-toggle-group';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { personsViewMessages, SourceFilter } from './persons-view-messages';

type PersonsViewFiltersProps = {
  filter: SourceFilter;
  onFilterChange: (filter: SourceFilter) => void;
};

/**
 * Filter toolbar above the Personas results table.
 *
 * Exclusive chip selectors: all people, with face, with voice, or unidentified
 * only. Controls which lanes appear in the table below. Clicking the active
 * non-default chip returns to "all".
 */
export function PersonsViewFilters({ filter, onFilterChange }: PersonsViewFiltersProps) {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const options: ChipToggleOption[] = [
    {
      value: 'all',
      label: formatMessage(personsViewMessages.filterAll),
      chipColor: theme.palette.primary.main,
      filledLabelColor: theme.palette.primary.contrastText,
      icon: <PeopleOutlinedIcon fontSize="small" />,
    },
    {
      value: 'face',
      label: formatMessage(personsViewMessages.filterFace),
      chipColor: theme.palette.info.main,
      filledLabelColor: theme.palette.info.contrastText,
      icon: <FaceOutlinedIcon fontSize="small" />,
    },
    {
      value: 'voice',
      label: formatMessage(personsViewMessages.filterVoice),
      chipColor: theme.palette.secondary.main,
      filledLabelColor: theme.palette.secondary.contrastText,
      icon: <RecordVoiceOverOutlinedIcon fontSize="small" />,
    },
    {
      value: 'unknown',
      label: formatMessage(personsViewMessages.filterUnknown),
      chipColor: theme.palette.grey[600],
      filledLabelColor: theme.palette.common.white,
      icon: <PersonOffOutlinedIcon fontSize="small" />,
    },
  ];

  return (
    <ChipToggleGroup
      options={options}
      value={filter}
      chipSize="medium"
      allowDeselect
      deselectValue="all"
      onChange={(next) => onFilterChange(next as SourceFilter)}
      sx={{ mb: 1.5 }}
    />
  );
}
